import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosClient from '@/lib/auth';
import { useAuthStore } from '@/store/authStore';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';

interface AuthTokenUsedUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
}

interface AuthTokenUsed {
  success: boolean;
  message?: string;
  code?: string;
  errors?: string[];
  user?: AuthTokenUsedUser;
}

/**
 * Authenticates a user using a monitoring token
 * @param token The monitoring token to authenticate with
 * @returns Promise with the authentication result
 */
async function postTokenLoginAuth(token: string): Promise<AuthTokenUsed> {
  try {
    const response = await axiosClient.post('/Auth/monitoring/tokenlogin', {
      token,
    });
    return response.data;
  } catch (error: any) {
    // Return a standardized error response
    return {
      success: false,
      message: error.response?.data?.message || 'Authentication failed',
      errors: error.response?.data?.errors || [error.message || 'Unknown error'],
    };
  }
}

function OneLink() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const authStore = useAuthStore();
  const [tokenIsInvalid, setTokenIsInvalid] = useState<boolean>(false);

  console.log('OneLinkTM');

  const getTokenParts = (token: string): string[] => {
    try {
      return atob(decodeURIComponent(token)).split(',');
    } catch (e) {
      return [];
    }
  };

  const getRawToken = (tokenParts: string[]): string => {
    if (tokenParts.length >= 2) {
      const rawTokenString = tokenParts[1];
      if (rawTokenString.startsWith('token=')) {
        return rawTokenString.substring(6);
      }
    }
    return '';
  };

  const getContext = (tokenParts: string[]): { type: string; id: string } | null => {
    if (tokenParts.length >= 4) {
      const rawTokenString = tokenParts[3];
      if (rawTokenString.startsWith('context=')) {
        const contextStr = rawTokenString.split('=')[1];
        const [type, id] = contextStr.split('|');
        return { type, id };
      }
    }
    return null;
  };

  async function verifyToken(): Promise<void> {
    if (!token) {
      setTokenIsInvalid(true);
      return;
    }

    const tokenSplit = getTokenParts(token);
    const rawToken = getRawToken(tokenSplit);
    const context = getContext(tokenSplit);

    try {
      const result = await postTokenLoginAuth(rawToken);

      console.log(result.user);

      if (result.success && result.user) {
        await authStore.loginOneLink(result.user);

        if (context) {
          if (context.type === 'alert') {
            navigate(`/alert/response/${context.id}`);
            return;
          }
        }
        navigate('/');
      }
    } catch (error) {
      const err = error as Error;
      setTokenIsInvalid(true);
    }
  }

  useEffect(() => {
    verifyToken();
  }, []);

  if (!tokenIsInvalid) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#111]">
        <Loader2 className="h-8 w-8 animate-spin text-white" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#111]">
      <Card className="w-full max-w-md">
        <CardHeader>
          <h2 className="text-xl font-medium text-white">Token is invalid</h2>
        </CardHeader>
        <CardContent>
          <div className="py-2 text-white/70">
            Either this token has been used or it has expired. Request a new one from the link
            below.
          </div>
          <div className="mt-4 flex justify-end">
            <Button variant="outline" onClick={() => navigate('/login')}>
              To request page
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default OneLink;
